import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { SyntheticEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { resetStockInput, setStockInputExpand, setSelectedStockId, setTriggerFocusCompanyNameInput, setStockInput, setDate, setStockItems, setDescription, setItems, setTotalValue, setBuyAmount, setSellAmount } from './reducers/StockInputReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { StockInputSelector } from './reducers/StockInputReducer';
import StockApi from './api/StockApi';
import StockModel from './models/StockModel';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import { setTriggerRefreshStatitic } from './reducers/StockSearchReducer';

const StockInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const StockInputViewData = useSelector(StockInputSelector);

    useEffect(() => {
        if (StockInputViewData.triggerSave > 0) {
            save();
        }
    }, [StockInputViewData.triggerSave]);

    useEffect(() => {
        dispatch(showLoading(true));
        StockApi.GetStockItems().then((response: BaseResponse<string[]>) => {
            if (response.success) {
                dispatch(setStockItems(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    }, []);

    useEffect(() => {
        if (StockInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [StockInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetStockInput());
        dispatch(setSelectedStockId(""));
        dispatch(setTriggerFocusCompanyNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (StockInputViewData.selectedStockId.length > 0) {
            selectStockById(StockInputViewData.selectedStockId);
        }
    }, [StockInputViewData.selectedStockId])

    const selectStockById = (id: string) => {
        dispatch(showLoading(true));
        StockApi.GetById({
            id: id
        }).then((response: BaseResponse<StockModel>) => {
            if (response.success) {
                dispatch(setStockInput(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const save = () => {
        try {
            validate();

            StockApi.Save(StockInputViewData.input)
                .then((response: BaseResponse<StockModel>) => {
                    if (response.success) {
                        if (StockInputViewData.input.id.length === 0) {
                            Utility.Alert.Success("Stock added successful");
                        } else {
                            Utility.Alert.Success("Stock updated successful");
                        }

                        refreshInput();

                        dispatch(setTriggerRefreshStatitic(new Date().getTime()));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (StockInputViewData.input.date.length === 0) {
            throw "Date is required";
        }

        if (StockInputViewData.input.items.length === 0) {
            throw "Items is required";
        }

        if (StockInputViewData.input.description.length === 0) {
            throw "Description is required";
        }

        if ((!StockInputViewData.input.sellAmount || StockInputViewData.input.sellAmount === 0) &&
            (!StockInputViewData.input.buyAmount || StockInputViewData.input.buyAmount === 0)) {
            throw "Buy or sell amount is required";
        }

        if (!StockInputViewData.input.totalValue || StockInputViewData.input.totalValue === 0) {
            throw "Total value is required";
        }
    }

    return (
        <>
            {
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    open={StockInputViewData.StockInputExpand}
                    disableHideButton={true}
                    onOpenChange={(isOpen: boolean) => {
                        dispatch(setStockInputExpand(isOpen));
                    }}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container>
                                <Grid xs={10} lg={9}>
                                    <StartEndDatepicker
                                        startDateLabel="Date"
                                        useQuickEndDateInput={false}
                                        className='cargo-input-component'
                                        onStartDateChange={(date: string) => {
                                            if (date) {
                                                dispatch(setDate(date));
                                            }
                                        }}
                                        startDateValue={Utility.DateFormat.formatDateString(StockInputViewData.input.date).toUpperCase()}
                                        useSingleMode={true}
                                    >
                                    </StartEndDatepicker>
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Description"
                                value={StockInputViewData.input.description ? StockInputViewData.input.description : ""}
                                onChange={(event) => {
                                    dispatch(setDescription(event.target.value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <Autocomplete
                                className='cargo-input-component'
                                freeSolo={true}
                                options={StockInputViewData.stockItems}
                                renderInput={(params) => <TextField {...params} label="Items" />}
                                value={StockInputViewData.input.items}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setItems(value ? value : ""));
                                }}
                                onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                                    dispatch(setItems(value ? value : ""));
                                }}
                            />
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Buy Amount"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={StockInputViewData.input.buyAmount ? StockInputViewData.input.buyAmount : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setBuyAmount(value));
                                }}
                            >
                            </TextField>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Sell Amount"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={StockInputViewData.input.sellAmount ? StockInputViewData.input.sellAmount : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setSellAmount(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Total Value"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={StockInputViewData.input.totalValue ? StockInputViewData.input.totalValue : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setTotalValue(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={1} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default StockInput;